<template>
  <!-- HeaderElement.vue -->
  <header class="header">
    <div class="header__left">
      <div @click="captureEventNavbarLogoClicked">
        <router-link to="/">
          <img
            class="header__left__logo"
            :src="require('@/assets/images/nomination-logo-white-light.png')"
          >
        </router-link>
      </div>
      <quick-search-element v-if="$can('read', PERMISSIONS.QUICK_SEARCH)" />
    </div>

    <div class="header__right">
      <nav class="header__right__links">
        <div
          v-if="$can('read', PERMISSIONS.ADVANCED_SEARCH)"
          class="header__right__links__link-box"
        >
          <a
            :href="`${configuration.desk_v1_host}/advanced-search`"
            class="header__right__links__link-box__item"
          >
            {{ $t('advanced_search') }}
          </a>
        </div>
        <div
          v-if="$can('read', PERMISSIONS.BUSINESS_ALERTS)"
          class="header__right__links__link-box"
        >
          <router-link
            class="header__right__links__link-box__item"
            to="/business-alerts"
          >
            {{ $t('business_alerts') }}
          </router-link>
        </div>
        <div
          v-if="$can('read', PERMISSIONS.CONFERENCES)"
          class="header__right__links__link-box"
        >
          <router-link
            class="header__right__links__link-box__item"
            :to="{ name: 'ConferencesPage' }"
          >
            {{ $t('conferences') }}
          </router-link>
        </div>
        <div
          v-if="$can('read', PERMISSIONS.NAVBAR_PROSPECTS)"
          class="header__right__links__link-box"
        >
          <a
            v-if="!is_license_insight && !is_license_smart_data"
            :to="{ path: '/my-prospecting/dashboard' }"
            class="header__right__links__link-box__item"
          >
            {{ $t('prospecting') }}
          </a>
          <a
            v-if="is_license_insight || is_license_smart_data"
            :to="{ path: '/my-prospecting/dashboard' }"
            class="header__right__links__link-box__item"
          >
            {{ $t('dashboard') }}
          </a>

          <nav
            class="my-prospecting-sub-header sub-header-conditional"
            :class="{'my-prospecting-sub-header--right-hide': !can_access_manager}"
          >
            <div class="my-prospecting-sub-header-left">
              <div
                v-if="$can('read', PERMISSIONS.PERSONAL_SPACE_DASHBOARD)"
                class="my-prospecting-sub-header-left-block"
                @click="$filters.goTo('/my-prospecting/dashboard');"
              >
                <span class="my-prospecting-sub-header-icon">
                  <svg viewBox="0 0 20 20">
                    <use :href="require('@/assets/images/dashboard.svg') + '#Desk'" />
                  </svg>
                </span>
                <div class="my-prospecting-sub-header-left-block-description">
                  <span class="my-prospecting-sub-header-left-block-text my-prospecting-sub-header-left-block-text--title">
                    {{ $t('dashboard') }}
                  </span>
                  <span class="my-prospecting-sub-header-left-block-text">
                    {{ $t('dashboard_text') }}
                  </span>
                </div>
              </div>
              <a
                v-if="$can('read', PERMISSIONS.MY_PROSPECTING_SMART_SCORING)"
                class="my-prospecting-sub-header-left-block"
                :href="`${configuration.desk_v1_host}/my-prospecting/smart-prospect`"
              >
                <span class="my-prospecting-sub-header-icon">
                  <svg viewBox="0 0 20 20">
                    <use :href="require('@/assets/images/grow-up.svg') + '#Desk'" />
                  </svg>
                </span>
                <div class="my-prospecting-sub-header-left-block-description">
                  <span class="my-prospecting-sub-header-left-block-text my-prospecting-sub-header-left-block-text--title">
                    {{ $t('smart_scoring') }}
                  </span>
                  <span class="my-prospecting-sub-header-left-block-text">
                    {{ $t('smart_scoring_text') }}
                  </span>
                </div>
              </a>
            </div>
            <div
              v-if="can_access_manager"
              class="my-prospecting-sub-header-right"
            >
              <div
                class="my-prospecting-sub-header-right-first-block"
              >
                <span class="my-prospecting-sub-header-icon">
                  <svg viewBox="0 0 20 20">
                    <use :href="require('@/assets/images/cog.svg') + '#Desk'" />
                  </svg>
                </span>
                <div>
                  <span
                    class="my-prospecting-sub-header-right-first-block-text my-prospecting-sub-header-right-first-block-text--title"
                  >
                    {{ $t('manager_access') }}
                  </span>

                  <router-link
                    class="my-prospecting-sub-header-link"
                    to="/my-prospecting/team-monitoring"
                  >
                    {{ $t('team_tracking') }}
                  </router-link>
                  <router-link
                    class="my-prospecting-sub-header-link"
                    to="/my-prospecting/lists-monitoring"
                  >
                    {{ $t('prospecting_list') }}
                  </router-link>
                </div>
              </div>
            </div>
          </nav>
        </div>
        <div
          class="header__right__links__link-box"
        >
          <router-link
            v-if="!is_license_sender && !is_license_sender_v2 && !is_license_connector"
            to="/my-lists"
            class="header__right__links__link-box__item"
          >
            {{ $t('my_lists') }}
          </router-link>
        </div>
        <div
          v-if="$can('read', PERMISSIONS.CAMPAIGNS) && $can('read', PERMISSIONS.SEQUENCES)"
          class="header__right__links__link-box"
        >
          <div
            class="header__right__links__link-box__item"
          >
            {{ $t('campaigns_and_sequences') }}
          </div>
          <nav class="campaigns-sequences-sub-header sub-header-conditional">
            <div class="campaigns-sequences-sub-header-left">
              <div class="campaigns-sequences-sub-header-left-first-block">
                <span class="campaigns-sequences-sub-header-icon">
                  <svg viewBox="0 0 20 20">
                    <use :href="require('@/assets/icons/bullhorn.svg') + '#Desk'" />
                  </svg>
                </span>
              </div>
              <div>
                <span class="campaigns-sequences-sub-header-text campaigns-sequences-sub-header-link--title">
                  {{ $t('my_campaigns') }}
                </span>
                <router-link
                  class="campaigns-sequences-sub-header-link"
                  to="/campaigns/results"
                >
                  {{ $t('assessment') }}
                </router-link>
                <router-link
                  class="campaigns-sequences-sub-header-link"
                  to="/campaigns/lists/sent"
                >
                  {{ $t('lists') }}
                </router-link>
                <router-link
                  v-if="$can('read', PERMISSIONS.CAMPAIGNS_SETTINGS)"
                  class="campaigns-sequences-sub-header-link"
                  to="/campaigns/settings"
                >
                  {{ $t('configuration') }}
                </router-link>
              </div>
            </div>
            <div class="campaigns-sequences-sub-header-right">
              <div class="campaigns-sequences-sub-header-right-first-block">
                <span class="campaigns-sequences-sub-header-icon">
                  <v-icon size="30">
                    mdi-play-circle
                  </v-icon>
                </span>
              </div>
              <div>
                <span class="campaigns-sequences-sub-header-text campaigns-sequences-sub-header-link--title">
                  {{ $t('my_sequences') }}
                </span>
                <router-link
                  class="campaigns-sequences-sub-header-link"
                  to="/sequences/results"
                >
                  {{ $t('assessment') }}
                </router-link>
                <router-link
                  class="campaigns-sequences-sub-header-link"
                  to="/sequences/lists/ongoing"
                >
                  {{ $t('lists') }}
                </router-link>
                <router-link
                  v-if="$can('read', PERMISSIONS.SEQUENCES_SETTINGS)"
                  class="campaigns-sequences-sub-header-link"
                  to="/sequences/settings"
                >
                  {{ $t('configuration') }}
                </router-link>
              </div>
            </div>
          </nav>
        </div>
      </nav>

      <div
        v-if="$can('read', PERMISSIONS.PERSONAL_SPACE_NOTIFICATIONS)"
        class="notifications-icon"
        @click="toggleNotifications()"
      >
        <v-icon size="small">
          mdi-bell
        </v-icon>

        <div
          v-if="notification_count > 0"
          class="notifications-count"
        >
          {{ notification_count }}
        </div>
      </div>

      <div
        v-if="show_notifications"
        v-click-outside="closeNotifications"
        class="notifications-container"
      >
        <notification-element
          v-for="notification in notification_not_viewed_list"
          :key="notification.id"
          class="notification-item"
          :notification="notification"
          small
        />

        <div
          v-if="notification_count === 0"
          class="notification-empty nds-text-p"
        >
          {{ $t('no_notification') }}
        </div>

        <router-link
          v-if="$can('read', PERMISSIONS.PERSONAL_SPACE_NOTIFICATIONS)"
          class="notification-link nds-text-link"
          :to="{ path: '/personal-space/notifications'}"
          @click="closeNotifications"
        >
          <svg-element
            icon="arrow-right"
            size="12"
          />

          {{ $t('see_more_notification') }}
        </router-link>
      </div>

      <div
        class="header__right__user"
        :class="{ 'header__right__user--connected-as': is_connected_as }"
        @mouseover="show_user_submenu=true"
        @mouseleave="show_user_submenu=help_popup || false"
      >
        <nav>
          <a>
            <img
              class="header__right__user__avatar"
              :src="require('@/assets/images/dummy/avatar.png')"
            >
          </a>
        </nav>

        <nav
          v-if="user_data !== null"
          class="header__right__user__identity"
        >
          <a
            class="header__right__user__identity__first-name"
          >
            {{ user_data.first_name }}
          </a>
          <a
            class="header__right__user__identity__last-name"
          >
            {{ user_data.last_name }}
          </a>
        </nav>

        <div
          v-if="is_connected_as"
          class="connected-as"
        >
          <v-icon size="24">
            mdi-alert
          </v-icon>
        </div>

        <nav
          v-if="show_user_submenu"
          class="header__submenu submenu-right"
        >
          <router-link
            v-if="!is_license_sender && !is_license_sender_v2 && !is_license_connector"
            class="header__submenu__link"
            :to="{ path: '/personal-space/saved-searches'}"
          >
            <span class="header__submenu__link__icon">
              <v-icon size="small">mdi-magnify</v-icon>
            </span>
            <span>{{ $t('user-submenu_link_searches') }}</span>
          </router-link>

          <div
            v-if="$can('read', PERMISSIONS.PERSONAL_SPACE_ACTIVITY)"
            class="header__submenu__section"
          >
            <span class="header__submenu__section__title">
              {{ $t('user-submenu_title_activity') }}
            </span>

            <router-link
              v-if="$can('read', PERMISSIONS.PERSONAL_SPACE_NOTIFICATIONS)"
              class="header__submenu__link"
              :to="{ path: '/personal-space/notifications'}"
            >
              <span class="header__submenu__link__icon">
                <v-icon size="small">mdi-bell</v-icon>
              </span>
              <span>{{ $t('user-submenu_link_notifications') }}</span>
            </router-link>

            <router-link
              v-if="$can('read', PERMISSIONS.PERSONAL_SPACE_REQUESTS)"
              class="header__submenu__link"
              :to="{ path: '/reports'}"
            >
              <span class="header__submenu__link__icon">
                <v-icon size="small">mdi-binoculars</v-icon>
              </span>
              <span>{{ $t('user-submenu_my_requests_and_inquiries') }}</span>
            </router-link>

            <router-link
              v-if="$can('read', PERMISSIONS.PERSONAL_SPACE_EXPORT) && can_export"
              class="header__submenu__link"
              to="/personal-space/exports"
            >
              <span class="header__submenu__link__icon">
                <v-icon size="small">mdi-download</v-icon>
              </span>
              <span>{{ $t('user-submenu_link_exports') }}</span>
            </router-link>

            <router-link
              v-if="can_export_email_rental"
              class="header__submenu__link"
              to="/personal-space/email-rental"
            >
              <span class="header__submenu__link__icon">
                <v-icon size="small">mdi-download</v-icon>
              </span>
              <span>{{ $t('user-submenu_link_email_rental') }}</span>
            </router-link>

            <router-link
              v-if="$can('read', PERMISSIONS.PERSONAL_SPACE_CREDIT_CONSUMPTION)"
              class="header__submenu__link"
              to="/personal-space/credit-consumption"
            >
              <span class="header__submenu__link__icon">
                <v-icon size="small">mdi-database</v-icon>
              </span>
              <span>{{ $t('user-submenu_link_credits') }}</span>
            </router-link>
          </div>

          <div
            v-if="$can('read', PERMISSIONS.PERSONAL_SPACE_PARAMETERS)"
            class="header__submenu__section"
          >
            <span class="header__submenu__section__title">{{ $t('user-submenu_title_configuration') }}</span>

            <router-link
              v-if="$can('read', PERMISSIONS.PERSONAL_SPACE_PROFILE_AND_AI)"
              class="header__submenu__link"
              to="/personal-space/profile"
            >
              <span class="header__submenu__link__icon">
                <v-icon size="small">mdi-account</v-icon>
              </span>
              <span>{{ user_data?.can_see_ai ? $t('user-submenu_link_profile') : $t('user-submenu_link_profile_only') }}</span>
            </router-link>

            <router-link
              class="header__submenu__link"
              to="/personal-space/my-contract"
            >
              <span class="header__submenu__link__icon">
                <svg-element
                  class="icon"
                  icon="contract"
                  size="12"
                />
              </span>
              <span>{{ $t('user-submenu_link_my_contract') }}</span>
            </router-link>

            <router-link
              v-if="$can('read', PERMISSIONS.PERSONAL_SPACE_ACCOUNT_SEGMENTS)
                && $can('read', PERMISSIONS.BUSINESS_ALERTS)"
              class="header__submenu__link"
              to="/personal-space/company-segments"
            >
              <span class="header__submenu__link__icon">
                <v-icon size="small">mdi-office-building</v-icon>
              </span>
              <span>{{ $t('user-submenu_link_companies') }}</span>
            </router-link>

            <router-link
              v-if="$can('read', PERMISSIONS.PERSONAL_SPACE_CONTACT_SEGMENTS)
                && $can('read', PERMISSIONS.BUSINESS_ALERTS)"
              class="header__submenu__link"
              to="/personal-space/contact-segments"
            >
              <span class="header__submenu__link__icon">
                <v-icon size="small">mdi-card-account-details-outline</v-icon>
              </span>
              <span>{{ $t('user-submenu_link_contacts') }}</span>
            </router-link>

            <router-link
              v-if="$can('read', PERMISSIONS.PERSONAL_SPACE_MY_ALERTS)"
              class="header__submenu__link"
              to="/personal-space/alerts"
            >
              <span class="header__submenu__link__icon">
                <v-icon size="small">mdi-flash</v-icon>
              </span>
              <span>{{ $t('user-submenu_link_alerts') }}</span>
            </router-link>

            <router-link
              v-if="$can('read', PERMISSIONS.PERSONAL_SPACE_TEMPLATES)"
              class="header__submenu__link"
              to="/personal-space/templates"
            >
              <span class="header__submenu__link__icon">
                <v-icon size="small">mdi-card-text-outline</v-icon>
              </span>

              <span
                v-if="is_license_smart_campaign
                  || is_license_smart_campaign_v2
                  || is_license_sales_and_marketing"
              >
                {{ $t('user-submenu_link_templates-and-signatures') }}
              </span>
              <span v-else>
                {{ $t('user-submenu_link_templates-and-signature') }}
              </span>
            </router-link>

            <router-link
              v-if="$can('read', PERMISSIONS.PERSONAL_SPACE_TEMPLATES) && !is_license_sender && !is_license_sender_v2"
              class="header__submenu__link"
              to="/personal-space/bat"
            >
              <span class="header__submenu__link__icon">
                <v-icon size="small">mdi-at</v-icon>
              </span>
              <span>{{ $t('user-submenu_link_test-emails') }}</span>
            </router-link>
          </div>

          <div
            v-if="$can('read', PERMISSIONS.PERSONAL_SPACE_MY_ACCOUNT)"
            class="header__submenu__section"
          >
            <span class="header__submenu__section__title">{{ $t('user-submenu_title_account') }}</span>

            <router-link
              v-if="$can('read', PERMISSIONS.PERSONAL_SPACE_EMAIL_CONNECTION)"
              class="header__submenu__link"
              to="/personal-space/connections"
            >
              <span class="header__submenu__link__icon">
                <v-icon size="small">mdi-cog</v-icon>
              </span>
              <span>{{ $t('user-submenu_link_external_connection') }}</span>
            </router-link>

            <router-link
              v-if="$can('read', PERMISSIONS.PERSONAL_SPACE_NOTIFICATIONS_SETTINGS)"
              class="header__submenu__link"
              to="/personal-space/notifications/settings"
            >
              <span class="header__submenu__link__icon">
                <v-icon size="small">mdi-bell</v-icon>
              </span>
              <span>{{ $t('user-submenu_link_preferences') }}</span>
            </router-link>

            <router-link
              v-if="$can('read', PERMISSIONS.PERSONAL_SPACE_CHANGE_PASSWORD)"
              class="header__submenu__link"
              to="/personal-space/change-password"
            >
              <span class="header__submenu__link__icon">
                <v-icon size="small">mdi-lock</v-icon>
              </span>
              <span>{{ $t('user-submenu_link_password') }}</span>
            </router-link>

            <a
              v-if="$can('read', PERMISSIONS.PERSONAL_SPACE_LOGOUT)"
              class="header__submenu__link"
              @click="goLogout()"
            >
              <span class="header__submenu__link__icon">
                <v-icon size="small">mdi-power</v-icon>
              </span>
              <span>{{ $t('user-submenu_link_logout') }}</span>
            </a>
          </div>

          <div class="header__submenu__section">
            <help-popup @close="handleHelpPopupClosed">
              <template #trigger>
                <div class="contact-us">
                  <button-element
                    :text="$t('contact_us')"
                    @click="help_popup = true"
                  />
                </div>
              </template>
            </help-popup>
          </div>
        </nav>
      </div>
    </div>
  </header>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import configuration from '@/configurations';

import {
  CUSTOM_CAPTURE_EVENTS,
  LICENSE_CONSTANTS,
  PERMISSIONS,
  WEBSOCKET_TYPES,
} from '@/constants';

import ButtonElement from '@/components/elements/ButtonElement.vue';
import HelpPopup from '@/components/elements/HelpPopup.vue';
import NotificationElement from '@/components/elements/NotificationElement.vue';
import QuickSearchElement from '@/components/elements/quick_search/QuickSearchElement.vue';
import SvgElement from '@/components/elements/SvgElement.vue';

export default {
  name: 'HeaderElement',
  components: {
    ButtonElement,
    HelpPopup,
    NotificationElement,
    QuickSearchElement,
    SvgElement,
  },
  data: () => ({
    configuration,
    show_notifications: false,
    show_user_submenu: false,
    help_popup: false,
    PERMISSIONS,
  }),
  computed: {
    ...mapGetters({
      is_connected_as: 'user/isConnectedAs',
      notification_not_viewed_list: 'notification/notificationNotViewedList',
      user_data: 'user/userData',
    }),
    can_access_manager() {
      return this.user_data.is_organization_manager && this.$can('read', PERMISSIONS.MY_PROSPECTING_MANAGER_ACCESS);
    },
    can_export() {
      return this.user_data.can_export;
    },
    can_export_email_rental() {
      return this.user_data.can_export_location;
    },
    is_license_insight() {
      // eslint-disable-next-line vue/max-len
      return this.user_data.organization_user_license_list[0].license.id === LICENSE_CONSTANTS.INSIGHT.ID;
    },
    is_license_sales_and_marketing() {
      // eslint-disable-next-line vue/max-len
      return this.user_data.organization_user_license_list[0].license.id === LICENSE_CONSTANTS.SALES_AND_MARKETING.ID;
    },
    is_license_sender() {
      // eslint-disable-next-line vue/max-len
      return this.user_data.organization_user_license_list[0].license.id === LICENSE_CONSTANTS.SENDER.ID;
    },
    is_license_sender_v2() {
      // eslint-disable-next-line vue/max-len
      return this.user_data.organization_user_license_list[0].license.id === LICENSE_CONSTANTS.SENDER_V2.ID;
    },
    is_license_connector() {
      // eslint-disable-next-line vue/max-len
      return this.user_data.organization_user_license_list[0].license.id === LICENSE_CONSTANTS.CONNECTOR.ID;
    },
    is_license_smart_campaign() {
      // eslint-disable-next-line vue/max-len
      return this.user_data.organization_user_license_list[0].license.id === LICENSE_CONSTANTS.MARKETING.ID;
    },
    is_license_smart_campaign_v2() {
      // eslint-disable-next-line vue/max-len
      return this.user_data.organization_user_license_list[0].license.id === LICENSE_CONSTANTS.SMART_CAMPAIGN_V2.ID;
    },
    is_license_smart_data() {
      // eslint-disable-next-line vue/max-len
      return this.user_data.organization_user_license_list[0].license.id === LICENSE_CONSTANTS.SMART_DATA.ID;
    },
    notification_count() {
      return this.notification_not_viewed_list.length;
    },
  },
  watch: {
    show_notifications() {
      if (!this.show_notifications) {
        this.getNotificationsNotViewed();
      }
    },
  },
  mounted() {
    this.getCredentialByUserId(this.user_data.id);
    this.getNotificationsNotViewed();

    this.onMessage({
      callback: () => {
        this.getNotificationsNotViewed();
      },
      type_id: WEBSOCKET_TYPES.NOTIFICATION,
    });
  },
  methods: {
    ...mapActions({
      captureEvent: 'event_capture/capture',
      getCredentialByUserId: 'nylas/getCredentialByUserId',
      logout: 'user/logout',
      getNotificationsNotViewed: 'notification/getNotificationsNotViewed',
      onMessage: 'websocket/onMessage',
      setNotificationsAsViewed: 'notification/setNotificationsAsViewed',
    }),
    closeNotifications() {
      this.show_notifications = false;
    },
    goLogout() {
      this.logout();
    },
    /**
     * this is to demo custom event capture ability
     */
    captureEventNavbarLogoClicked() {
      this.captureEvent({ event: CUSTOM_CAPTURE_EVENTS.NAVBAR_LOGO_CLICKED });
    },
    handleHelpPopupClosed() {
      this.help_popup = false;
      this.show_user_submenu = false;
    },
    toggleNotifications() {
      this.show_notifications = !this.show_notifications;

      if (this.show_notifications) {
        this.setNotificationsAsViewed();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  @import '@/constants/variables.module.scss';

  .submenu-right {
    right: 0;
  }

  .my-prospecting-sub-header {
    display: flex;
    width: 437px;
    left: 0;
    top: 48px;
    box-shadow: 0 2px 4px 0 rgb(0 0 0 / 25%);
    position: absolute;
    background-color: $white;

    &--right-hide {
      width: 220px;
      left: 0;
      position: absolute;
    }
  }

  .my-prospecting-sub-header-icon {
    padding: 8px 0px 10px 15px;
    i {
      color: $blue-haze !important;
    }

    svg {
      fill: $blue-haze;
      width: 40px;
    }
  }

  .my-prospecting-sub-header-left {
    display: flex;
    flex-direction: column;
    flex: 1;
  }

  .my-prospecting-sub-header-left-block {
    display: flex;
    border-bottom: 1px solid $medium-gray;
    padding-top: 8px;
    text-decoration: none;

    &:hover {
      cursor: pointer;
      background-color: $quartz;

      i {
        color: $cornflower-blue;
      }

      svg {
        fill: $cornflower-blue;
      }
    }
  }

  .my-prospecting-sub-header-left-block-description {
    margin-right: 10px;
  }

  .my-prospecting-sub-header-left-block-text {
    align-items: center;
    display: flex;
    color: $blue-zodiac;
    font-size: 10px;
    font-weight: 400;

    &--title {
      margin-top: 5px;
      font-size: 12px;
      font-weight: 500;
    }
  }

  .my-prospecting-sub-header-right {
    display: flex;
    border-left: 1px solid $medium-gray;
    border-bottom: 1px solid $medium-gray;
    flex: 1;

    &--hide {
      display: none;
    }

    &:hover {
      background-color: $quartz;

      i {
        color: $cornflower-blue;
      }

      svg {
        fill: $cornflower-blue;
      }
    }
  }

  .my-prospecting-sub-header-right-first-block {
    display: flex;
    padding: 8px 0px;
  }

  .my-prospecting-sub-header-right-first-block-text {
    align-items: center;
    display: flex;
    color: $blue-zodiac;
    font-size: 10px;
    font-weight: 400;

    &--title {
      margin-top: 5px;
      margin-bottom: 5px;
      font-size: 12px;
      font-weight: 500;
    }
  }

  .my-prospecting-sub-header-link {
    align-items: center;
    display: flex;
    color: $blue-zodiac;
    font-size: 10px;
    font-weight: 500;
    outline: none;
    text-decoration: none;
    padding-bottom: 5px;

    &--title {
      margin-top: 5px;
      font-size: 12px;
      font-weight: 500;
    }

    &:hover {
      text-decoration: underline;
    }
  }

  .campaigns-sequences-sub-header {
    background-color: $white;
    box-shadow: 0 2px 4px 0 rgb(0 0 0 / 25%);
    position: absolute;
    top: 48px;
    width: 380px;
    display: flex;
  }

  .campaigns-sequences-sub-header-icon {
    margin-top: 25px;
    i {
      color: $blue-haze !important;
    }

    svg {
      fill: $blue-haze;
      width: 30px;
    }
  }

  .campaigns-sequences-sub-header-left {
    display: flex;
    padding: 8px 16px 5px 16px;
    flex: 1;

    &:hover {
      background-color: $quartz;

      i {
        color: $cornflower-blue;
      }

      svg {
        fill: $cornflower-blue;
      }
    }
  }

  .campaigns-sequences-sub-header-left-first-block {
    margin-top: 5px;
  }

  .campaigns-sequences-sub-header-right {
    display: flex;
    border-left: 1px solid $medium-gray;
    padding: 8px 16px 5px 16px;
    flex: 1;

    &:hover {
      background-color: $quartz;

      i {
        color: $cornflower-blue !important;
      }
    }
  }

  .campaigns-sequences-sub-header-right-first-block {
    margin-top: 5px;
  }
  .campaigns-sequences-sub-header-text {
    align-items: center;
    display: flex;
    color: $blue-zodiac;
    font-size: 10px;
    font-weight: 500;
    outline: none;
    text-decoration: none;
    padding-bottom: 5px;
  }

  .campaigns-sequences-sub-header-link {
    align-items: center;
    display: flex;
    color: $blue-zodiac;
    font-size: 10px;
    font-weight: 500;
    outline: none;
    text-decoration: none;
    padding-bottom: 5px;

    &--title {
      margin-top: 5px;
      font-size: 12px;
      font-weight: 500;
    }

    &:hover {
      text-decoration: underline;
    }
  }

  .campaigns-sequences-sub-header-icon {
    margin-right: 12px;
    text-align: center;
  }

  .header {
    align-items: center;
    background-color: $space-cadet;
    display: flex;
    height: 50px;
    justify-content: space-between;
    padding: 0 15px;
    position: relative;
    z-index: map-get($zindex, header);

    &__submenu {
      background-color: $white;
      border-radius: 3px;
      box-shadow: 0 2px 4px 0 rgb(0 0 0 / 25%);
      padding: 10px;
      position: absolute;
      top: 50px;
      width: 300px;

      &__link {
        align-items: center;
        color: $amethyst-smoke;
        display: flex;
        font-size: 12px;
        font-weight: 500;
        padding: 8px;
        outline: none;
        text-decoration: none;

        &__icon {
            margin-right: 12px;
            text-align: center;
        }

        &:deep(i) {
          color: $amethyst-smoke !important;
        }

        &:deep(.email-not-connected) {
          color: $ripe-pumpkin !important;
        }

        &:hover {
          border-right: 3px solid $cornflower-blue;
          color: $haiti;

            &:deep(i) {
              color: $cornflower-blue !important;
            }

            &:deep(.email-not-connected) {
              color: $ripe-pumpkin !important;
            }
        }
      }

      &__section {
        margin-top: 10px;

        &__title {
          color: $blue-haze;
          font-size: 12px;
          letter-spacing: 2px;
          margin-bottom: 8px;
          margin-left: 0px;
          text-transform: uppercase;
        }
      }
    }

    &__left {
      align-items: center;
      display: flex;
      justify-content: space-between;

      &__logo {
        margin-right: 5px;
        width: 29px;
      }

      &__tagline {
        color: $white;
        font-size: 12px;
        margin: auto;
      }
    }

    &__right {
        align-items: center;
        display: flex;
        justify-content: space-between;

      &__links {
        align-items: center;
        display: flex;
        justify-content: space-between;

        &__link-box {
          align-items: center;
          border-bottom: 2px solid transparent;
          display: flex;
          height: 50px;
          justify-content: center;
          padding-top: 2px;
          position: relative;

          &:hover {
            border-bottom: 2px solid $white;
          }

          &__item {
            color: $heather;
            font-size: 12px;
            padding: 8px;
            text-decoration: none;
            transition: color 0.2s linear;

            &:hover {
                color: $white;
            }
          }
        }
      }

      &__user {
        align-items: center;
        display: flex;
        justify-content: center;
        height: 50px;
        margin-left: 10px;

        &:hover {
          border-top: 2px solid $space-cadet;
          border-bottom: 2px solid $white;
        }

        &__avatar {
          align-items: center;
          display: flex;
          border-radius: 50%;
          margin-right: 10px;
          width: 30px;
        }

        &__identity {
          align-items: left;
          display: flex;
          flex-direction: column;
          font-size: 12px;
          justify-content: space-between;

          &__first-name {
            color: $white;
            text-decoration: none;
          }

          &__last-name {
            color: $white;
            text-decoration: none;
            text-transform: uppercase;
          }
        }

        &--connected-as {
          .header__right__user__avatar {
            border: 2px solid $geraldine;
            box-sizing: border-box;
          }

          .header__right__user__identity__first-name,
          .header__right__user__identity__last-name {
            color: $geraldine;
          }
        }
      }
    }
  }

  .connected-as {
    margin-left: 10px;

    .v-icon {
      color: $burnt-sienna;
      font-size: 30px;
    }
  }

  .link-with-icon {
    display: flex;
    justify-content: space-between;
  }

  .email-not-connected {
    margin-left: 100px;
  }

  .header__right__links__link-box {
    & > .sub-header-conditional {
      display: none;
    }

    &:hover {
      & > .sub-header-conditional {
        display: flex;
      }
    }
  }

  .contact-us {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 20px;
    margin-bottom: 10px;
  }

  .notifications-icon {
    align-items: center;
    color: $white;
    cursor: pointer;
    display: flex;
    height: 50px;
    justify-content: center;
    padding: 10px;
    position: relative;

    svg {
      --color: #{$white};
    }
  }

  .notifications-count {
    align-items: center;
    background-color: $cornflower-blue;
    border-radius: 50%;
    color: $white;
    display: flex;
    height: 16px;
    justify-content: center;
    position: absolute;
    right: 0;
    top: 8px;
    width: 16px;

    font-family: 'DM Sans', sans-serif;
    line-height: 12px;
    font-size: 10px;
    font-weight: 500;
  }

  .notifications-container {
    background-color: $white;
    border: 1px solid $blue-haze;
    border-radius: 8px;
    box-shadow: 4px 4px 10px 0px rgba(0, 0, 0, 0.05);
    padding: 8px 24px;
    position: absolute;
    right: 40px;
    top: 62px;
    width: 540px;
  }

  .notification-item {
    border-bottom: 1px solid $blue-haze;
  }

  .notification-empty {
    border-bottom: 1px solid $blue-haze;
    color: $amethyst-smoke;
    padding-bottom: 16px;
    padding-top: 16px;
  }

  .notification-link {
    align-items: center;
    color: $cornflower-blue;
    display: flex;
    gap: 8px;
    justify-content: flex-end;
    margin-bottom: 16px;
    margin-top: 24px;
    text-decoration: none;

    svg {
      --color: #{$cornflower-blue};
    }
  }

.icon {
  --color: #{$amethyst-smoke};
}
</style>

<i18n src="@/locales/components/elements/header-element.json"></i18n>
